import { Container, IDestroyOptions, Rectangle, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { ICanvasScaler } from "../../../../../../../canvas/ICanvasScaler";
import { GameFont } from "../../../../../common/GameFont";
import { ButtonWithScaleAnimation } from "../../../../../common/components/ButtonWithScaleAnimation";
import { CommonResourcesPackage } from "../../../../../common/resources/CommonResourcesPackage";
import { FullBuildingMainButton } from "./FullBuildingMainButton";
import { MarketplaceGuestAvatar } from "./MarketplaceGuestAvatar";
import { FullBuildingInfoContainer } from "./FullBuildingInfoContainer";
import { DefaultButton } from "../../../../../common/components/DefaultButton";
import { Event } from "event-system";
import { IMarketplaceBuildingModel } from "../../models/building/IMarketplaceBuildingModel";
import { IMarketplaceController } from "../../models/controller/IMarketplaceController";
import { nameof } from "bindable-data";
import { FullBuilding } from "./FullBuilding";
import { IMarketplaceViewModel } from "../../models/IMarketplaceViewModel";
import { gsap } from "gsap";
import { IAnalyticProviderWrapper } from "../../../../../../../analytics/IAnalyticProviderWrapper";
import { VirtualScreenEvent } from "../../../../../../../analytics/events/VirtualScreenEvent";
import { MarketplaceCard } from "../MarketplaceCard";
import { UserProfileResourcePackage } from "../../../../userprofile/resources/UserProfileResourcePackage";
import { ILogger } from "../../../../../../logger/ILogger";
import { StringFormatExtention } from "../../../../../../../extensions/StringFormatExtention";
import { PaymentResult } from "../../../../../../../web3/PaymentResult";
import { IPlayerModel } from "../../../../../common/models/IPlayerModel";
import { BuildingRightsType } from "../../../../../../networking/data/responses/user/BuildingRightsType";
import { CancellationToken } from "async/CancellationToken";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { FullscreenInputBlocker } from "by/gamefactory/cityversetycoon/scenes/game/components/FullscreenInputBlocker";
import { MarketplaceResourcePackage } from "../../../resources/MarketplaceResourcePackage";
import { PriceHistoryButton } from "../../../../../common/components/PriceHistoryButton";
import { HBox } from "../../../../../common/components/ui/HBox";
type Timeline = gsap.core.Timeline;

export class FullBuildingContainer extends Container {
    private static readonly HEADER_TEXT = "MARKETPLACE";
    private static readonly REMOVE_LISTING = "REMOVE\nLISTING";
    private static readonly PURCHASE_PRICE = "PURCHASE PRICE: ";

    public readonly onLeftButtonClick: Event;
    public readonly onRightButtonClick: Event;
    public readonly onTransactionHandled: Event;

    private readonly cancellationToken: CancellationToken;
    private readonly marketplaceResources: MarketplaceResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly userProfileResources: UserProfileResourcePackage;
    private readonly canvasScaler: ICanvasScaler;
    private readonly marketplaceController: IMarketplaceController;
    private readonly marketplaceBuildingModel: IMarketplaceBuildingModel;
    private readonly marketplaceViewModel: IMarketplaceViewModel;
    private readonly marketplaceCards: Array<MarketplaceCard>;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly logger: ILogger;
    private readonly playerModel: IPlayerModel;
    private readonly web3Model: Web3Model;

    private viewBackground: Sprite;
    private headerText: Text;
    private initialCostText: Text;
    private guestAvatar: MarketplaceGuestAvatar;
    private infoContainer: FullBuildingInfoContainer;
    private buttonsBox: HBox;
    private mainButton: FullBuildingMainButton;
    private priceHistoryButton: PriceHistoryButton;

    private removeListingButton: ButtonWithScaleAnimation;
    private rightButton: ButtonWithScaleAnimation;
    private leftButton: ButtonWithScaleAnimation;
    private backButton: DefaultButton;
    private touchStartPosX: number;
    private isTouchMoveTracked: boolean;
    private building: FullBuilding;
    private sold: Sprite;
    private soldText: Text;
    private monIcon: Sprite;
    private isMy: boolean;
    private buildingArea: number;
    private fullScreenInputBlocker: FullscreenInputBlocker;
    private tutorialHideAnimation: Timeline;

    public constructor(
        cancellationToken: CancellationToken,
        marketplaceResources: MarketplaceResourcePackage,
        commonResources: CommonResourcesPackage,
        userProfileResources: UserProfileResourcePackage,
        canvasScaler: ICanvasScaler,
        marketplaceBuildingModel: IMarketplaceBuildingModel,
        marketplaceViewModel: IMarketplaceViewModel,
        marketplaceCards: Array<MarketplaceCard>,
        marketplaceController: IMarketplaceController,
        buildingArea: number,
        analyticsProvider: IAnalyticProviderWrapper,
        logger: ILogger,
        playerModel: IPlayerModel,
        web3Model: Web3Model
    ) {
        super();
        this.cancellationToken = cancellationToken;
        this.marketplaceResources = marketplaceResources;
        this.commonResources = commonResources;
        this.userProfileResources = userProfileResources;
        this.canvasScaler = canvasScaler;
        this.marketplaceBuildingModel = marketplaceBuildingModel;
        this.marketplaceViewModel = marketplaceViewModel;
        this.marketplaceCards = marketplaceCards;
        this.marketplaceController = marketplaceController;
        this.buildingArea = buildingArea;
        this.analyticsProvider = analyticsProvider;
        this.logger = logger;
        this.playerModel = playerModel;
        this.web3Model = web3Model;
        this.onLeftButtonClick = new Event();
        this.onRightButtonClick = new Event();
        this.onTransactionHandled = new Event();
        this.tutorialHideAnimation = new gsap.core.Timeline();
        this.build();

        this.building.on("pointermove", this.touchMoveHandle, this);
        this.building.on("pointerdown", this.touchStartHandle, this);
        this.mainButton.onUp.add(this.showSelectPaymentPopup, this);
        this.priceHistoryButton.onUp.add(this.priceHistoryClickHandle, this);
        this.removeListingButton.onUp.add(this.removeListingClickHandle, this);
        this.leftButton.onUp.add(this.leftButtonClickHandle, this);
        this.rightButton.onUp.add(this.rightButtonClickHandle, this);
        this.backButton.onUp.add(this.hide, this);

        this.marketplaceBuildingModel.propertyChanged.add(this.onMarketplaceBuildingModelPropertyChanged, this);
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.once("added", this.addedToStage.bind(this));
    }

    private async onMarketplaceBuildingModelPropertyChanged(model: IMarketplaceBuildingModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "currentBuilding"):
                this.guestAvatar.updateGuest(model.currentBuilding.value.guest);
                const buildingAdditionalData = this.marketplaceCards.find((item) => item.listItem.id === model.currentBuilding.value.id).listItem;
                this.isMy = buildingAdditionalData.my;
                this.updateInitialCost();
                this.sold.visible = buildingAdditionalData.sold;
                this.infoContainer.showInfoContainer(model.currentBuilding.value.building.tokensForPurchasing);
                const price = model.currentBuilding.value.price.mon != null ? `${StringFormatExtention.tokensFormat(model.currentBuilding.value.price.mon.toString())}` :
                    `$${StringFormatExtention.formatWithComma(model.currentBuilding.value.price.usd)}`;
                this.soldText.text = `SOLD ${price}`;
                this.soldText.position.x = model.currentBuilding.value.price.mon != null ? - this.monIcon.width * 0.47 : 0;
                this.monIcon.visible = model.currentBuilding.value.price.mon != null;
                this.monIcon.position.set(this.soldText.x + this.soldText.width * 0.5 + 5, this.soldText.y - this.monIcon.height + 10);
                this.mainButton.enabled = (model.currentBuilding.value.building.canBePurchased || this.isMy) && !buildingAdditionalData.sold;
                this.priceHistoryButton.visible = model.currentBuilding.value.purchaseHistory?.length > 0;
                this.removeListingButton.visible = this.isMy;

                if (buildingAdditionalData.my) {
                    this.mainButton.showEditStyle();
                }
                else {
                    this.mainButton.showPurchaseStyle();
                }

                this.buttonsBox.alignmentStrategy();
                break;
        }
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    private updateInitialCost(): void {
        const purchaseHistory = this.marketplaceBuildingModel.currentBuilding.value.purchaseHistory;
        this.initialCostText.visible = this.isMy && purchaseHistory?.length > 0;
        if (purchaseHistory?.length > 0) {
            const firstHistory = purchaseHistory[0];
            const price = firstHistory.price.usd != null ? firstHistory.price.usd.toFixed(2) : firstHistory.price.mon.toFixed(2);
            const currency = firstHistory.price.usd != null ? "$" : "MON";
            this.initialCostText.text = `${FullBuildingContainer.PURCHASE_PRICE}${currency}${StringFormatExtention.formatWithComma(price)}`;
            this.initialCostText.position.set(this.canvasScaler.referenceWidth * 0.5 - this.initialCostText.width * 0.5, 1076);
        }
    }

    private build(): void {
        this.viewBackground = new Sprite(this.commonResources.assets_common_blured_backgrounds_blurry_map_jpg);
        this.addChild(this.viewBackground);
        this.fullScreenInputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.fullScreenInputBlocker.blocked = false;
        this.building = new FullBuilding(this.marketplaceResources, this.marketplaceBuildingModel, this.marketplaceViewModel, this.fullScreenInputBlocker);
        this.building.eventMode = "static";
        this.addChild(this.building);
        this.initialCostText = new Text(`${FullBuildingContainer.PURCHASE_PRICE}$0`, new InitialCostTextStyle());
        this.addChild(this.initialCostText);
        this.guestAvatar = new MarketplaceGuestAvatar(Texture.WHITE, this.userProfileResources, "CHARLOTTE P.", this.canvasScaler);
        this.addChild(this.guestAvatar);
        this.infoContainer = new FullBuildingInfoContainer(this.commonResources, this.marketplaceBuildingModel);
        this.addChild(this.infoContainer);

        this.buttonsBox = new HBox(40);
        this.addChild(this.buttonsBox);

        this.mainButton = new FullBuildingMainButton(this.marketplaceResources, this.marketplaceBuildingModel, this.marketplaceCards);
        this.mainButton.defaultView.width = 297;
        this.mainButton.defaultView.height = 135;

        this.priceHistoryButton = new PriceHistoryButton(this.marketplaceResources.assets_views_marketplace_Price_history_icon_png);
        this.addChild(this.priceHistoryButton);

        this.removeListingButton = new ButtonWithScaleAnimation(this.marketplaceResources.assets_views_marketplace_Button_red_long_png);
        this.removeListingButton.defaultView.width = 297;
        this.removeListingButton.defaultView.height = 135;
        this.removeListingButton.visible = false;

        const removeListingText = new Text(FullBuildingContainer.REMOVE_LISTING, new RemoveListingTextStyle());
        removeListingText.position.set(
            (this.removeListingButton.width - removeListingText.width) * 0.5,
            (this.removeListingButton.height - removeListingText.height) * 0.5
        );
        this.removeListingButton.addChild(removeListingText);

        this.buttonsBox.addChild(this.mainButton, this.removeListingButton);

        this.leftButton = new ButtonWithScaleAnimation(this.marketplaceResources.assets_views_marketplace_swith_button_bg_png, 0.6);
        const leftArrow = new Sprite(this.marketplaceResources.assets_views_marketplace_x_cross_png);
        leftArrow.position.set(22, 23);
        this.leftButton.defaultView.addChild(leftArrow);
        this.addChild(this.leftButton);
        this.rightButton = new ButtonWithScaleAnimation(this.marketplaceResources.assets_views_marketplace_swith_button_bg_png, 0.6);
        const rightArrow = new Sprite(this.marketplaceResources.assets_views_marketplace_x_cross_png);
        rightArrow.scale.x = -1;
        rightArrow.position.set(76, 23);
        this.rightButton.defaultView.addChild(rightArrow);
        this.addChild(this.rightButton);
        this.backButton = new DefaultButton(this.commonResources.assets_common_ui_back_png);
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height, this.backButton.width * 2, this.backButton.height * 2);
        this.addChild(this.backButton);

        this.headerText = new Text(FullBuildingContainer.HEADER_TEXT, new HeaderTextStyle());
        this.addChild(this.headerText);
        this.sold = new Sprite(this.marketplaceResources.assets_views_marketplace_sold_bg_png);
        this.sold.anchor.set(0.5);
        this.soldText = new Text("SOLD $15", new SoldTextStyle());
        this.soldText.anchor.set(0.5);
        this.soldText.skew._x = -0.087;
        this.soldText.angle = -5;
        this.soldText.position.set(17, 10);
        this.sold.addChild(this.soldText);
        this.monIcon = new Sprite(this.marketplaceResources.assets_views_marketplace_mon_icon_png);
        this.monIcon.scale.set(1.5);
        this.sold.addChild(this.monIcon);
        this.monIcon.position.set(this.soldText.x, this.soldText.y);
        this.addChild(this.sold);
        this.addChild(this.fullScreenInputBlocker);
    }

    private addedToStage(): void {
        this.initialCostText.visible = false;
        this.sold.visible = false;
    }

    private async showSelectPaymentPopup(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        try {
            const currentBuilding = this.marketplaceBuildingModel.currentBuilding.value;
            if (this.isMy) {
                await this.marketplaceController.showEditPricePopup(
                    currentBuilding.id,
                    currentBuilding.building.tokenId,
                    currentBuilding.reservoirOrderId,
                    currentBuilding.price.usd,
                    currentBuilding.purchaseHistory,
                    this.cancellationToken
                );
            } else {
                this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Click on buy on building image in MP", this.analyticsProvider.currentEventIndex));
                const result = await this.marketplaceController.showSelectPaymentMethodPopup(currentBuilding, this.cancellationToken);
                await this.handlePaymentResult(result);
            }
        } catch (error: unknown) {
            this.logger.error(error);
        } finally {
            this.removeChild(inputBlocker);
        }
    }

    private async priceHistoryClickHandle(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        try {
            await this.marketplaceController.showPriceHistoryScene(this.marketplaceBuildingModel.currentBuilding.value.purchaseHistory);
        } catch (error: unknown) {
            this.logger.error(error);
        } finally {
            this.removeChild(inputBlocker);
        }
    }

    private async handlePaymentResult(result: PaymentResult): Promise<void> {
        try {
            if (result == null) {
                this.logger.debug("Building purchase cancelled");
                return;
            }
            if (result == PaymentResult.SUCCESS) {
                if (this.playerModel.buildingRights.value === BuildingRightsType.RENTER) {
                    location.reload();
                } else {
                    const currentListItem = this.marketplaceCards.find((item) => item.listItem.id === this.marketplaceBuildingModel.currentBuilding.value.id).listItem;
                    await this.marketplaceController.updatePlayerDataAndMarketplace(currentListItem);
                    await this.marketplaceController.showMyWalletPopup();
                }
            }
            else {
                const currentListItem = this.marketplaceCards.find((item) => item.listItem.id === this.marketplaceBuildingModel.currentBuilding.value.id).listItem;
                if (!await this.marketplaceController.updatePlayerDataAndMarketplace(currentListItem)) {
                    this.hide();
                }
                this.logger.debug(`Building purchase failed`);
            }
            this.onTransactionHandled.emit();
        }
        catch (e: unknown) {
            this.logger.error("Exception on handling payment result: ", e);
        }
    }

    private async removeListingClickHandle(): Promise<void> {
        const buildingData = this.marketplaceBuildingModel.currentBuilding.value;
        const result = await this.marketplaceController.showRemoveBuildingPopup(buildingData.id, buildingData.reservoirOrderId, this.cancellationToken);
        if (result) {
            this.hide();
        }
    }

    private async touchStartHandle(event: any): Promise<void> {
        this.touchStartPosX = Math.ceil(event.data.global.x);
        this.isTouchMoveTracked = true;
    }

    private async touchMoveHandle(event: any): Promise<void> {
        if (!this.isTouchMoveTracked) {
            return;
        }
        let currentTouchPosX: number = Math.ceil(event.data.global.x);
        const range = 60;
        const distance = currentTouchPosX - this.touchStartPosX;
        if (distance > range) {
            this.isTouchMoveTracked = false;
            this.leftButtonClickHandle();
        } else if (distance < -range) {
            this.isTouchMoveTracked = false;
            this.rightButtonClickHandle();
        }
    }

    private leftButtonClickHandle(): void {
        this.onLeftButtonClick.emit();
    }

    private rightButtonClickHandle(): void {
        this.onRightButtonClick.emit();
    }

    public async show(): Promise<void> {
        this.viewBackground.alpha = 1;
        this.building.showLoader();
        this.leftButton.visible = true;
        this.rightButton.visible = true;
        this.visible = true;
    }

    public async hide(): Promise<void> {
        this.infoContainer.hideContainer();
        if (this.buildingArea != -1 && this.isMy) {
            this.buildingArea = -1;
            this.tutorialHideAnimation.fromTo(
                this.buttonsBox.scale,
                { x: 1, y: 1 },
                { x: 0, y: 0, duration: 0.4, ease: "back.in(2)" }
            );
            this.tutorialHideAnimation.fromTo(
                this.priceHistoryButton,
                { width: this.priceHistoryButton.width, height: this.priceHistoryButton.height },
                { width: 0, height: 0, duration: 0.4, ease: "back.in(2)" },
                "<"
            );
            this.tutorialHideAnimation.fromTo(
                this.infoContainer,
                { width: this.infoContainer.width, height: this.infoContainer.height },
                { width: 0, height: 0, duration: 0.4, ease: "back.in(2)" },
                "<10%"
            );
            this.tutorialHideAnimation.fromTo(
                this.guestAvatar,
                { x: this.guestAvatar.x, width: this.guestAvatar.width, height: this.guestAvatar.height },
                { width: 0, height: 0, duration: 0.4, x: this.guestAvatar.x + this.guestAvatar.width * 0.4, ease: "back.in(2)" },
                "<10%"
            );
            this.tutorialHideAnimation.fromTo(
                this.initialCostText,
                { x: this.initialCostText.x, width: this.initialCostText.width, height: this.initialCostText.height },
                { width: 0, height: 0, duration: 0.4, x: this.initialCostText.x + this.initialCostText.width * 0.4, ease: "back.in(2)" },
                "<"
            );
            this.tutorialHideAnimation.fromTo(
                this.building,
                { angle: this.building.angle, width: this.building.width, height: this.building.height },
                { angle: -90, width: 0, height: 0, x: this.width * 0.44, y: this.height * 0.87, duration: 0.8, ease: "power2.in" },
                "<20%"
            );
            this.tutorialHideAnimation.fromTo(this.viewBackground, { alpha: 1 }, { alpha: 0, duration: 0.4, ease: "power2.in" }, "<");
            this.tutorialHideAnimation.set(this.leftButton, { visible: false }, "<");
            this.tutorialHideAnimation.set(this.rightButton, { visible: false }, "<");
            await this.tutorialHideAnimation.play();
            this.tutorialHideAnimation.progress(0);
            this.tutorialHideAnimation.clear();
        }
        this.visible = false;
    }

    private layout(width: number, _height: number): void {
        this.headerText.position.set(265, 105);
        this.backButton.position.set(59, 110);
        this.initialCostText.position.set(width * 0.5 - this.initialCostText.width * 0.5, 1076);
        this.guestAvatar.position.set(width * 0.5 - this.guestAvatar.width * 0.5, 1130);
        this.building.position.set(width * 0.5, 655);
        this.infoContainer.position.set(width * 0.5, 1276);
        this.buttonsBox.position.set((width - this.buttonsBox.width) * 0.5, 1500);
        this.priceHistoryButton.position.set(this.buttonsBox.position.x + (this.buttonsBox.width - this.priceHistoryButton.width) * 0.5, this.buttonsBox.position.y + this.buttonsBox.height + 15);
        this.sold.position.set(414, 1560);
        this.rightButton.position.set(698, 608);
        this.leftButton.position.set(32, 608);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.mainButton.onUp.remove(this.showSelectPaymentPopup, this);
        this.priceHistoryButton.onUp.remove(this.priceHistoryClickHandle, this);
        this.removeListingButton.onUp.remove(this.removeListingClickHandle, this);
        this.leftButton.onUp.remove(this.leftButtonClickHandle, this);
        this.rightButton.onUp.remove(this.rightButtonClickHandle, this);
        this.backButton.onUp.remove(this.hide, this);
        this.building.off("pointermove", this.touchMoveHandle, this);
        this.building.off("pointerdown", this.touchStartHandle, this);
        this.tutorialHideAnimation?.kill();
        this.marketplaceBuildingModel.propertyChanged.remove(this.onMarketplaceBuildingModelPropertyChanged, this);
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: [
                "#00ffa4",
                "#11c1e1"
            ],
            fillGradientType: 1,
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 74
        }));
    }
}

class RemoveListingTextStyle extends TextStyle {
    public constructor() {
        super({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 52,
            fontWeight: "400",
            lineHeight: 46,
            dropShadow: true,
            dropShadowAlpha: 0.3,
            dropShadowColor: 0x0d0606,
            dropShadowAngle: 0.7,
            dropShadowBlur: 5.3,
            dropShadowDistance: 6.5
        });
    }
}

class SoldTextStyle extends TextStyle {
    public constructor() {
        super({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 115
        });
    }
}

class InitialCostTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fill: [
                "#a8fd09",
                "#15ce14"
            ],
            fontSize: 42,
            align: "center",
        });
    }
}
